import { useCallback, useEffect, useState } from 'react';
import { partners as partnersApi, stores as storesApi } from '../api/endpoints';
import { useOrderEvents } from '../context/SocketContext';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { Loading, Empty, Modal, Can, Pagination, money, dateOnly } from '../components/Common';
import PartnerForm, { CredentialsModal } from './PartnerForm';

const KYC_TONE = { approved: 'green', rejected: 'red', submitted: 'amber', pending: 'gray' };

export default function Partners() {
  const toast = useToast();
  const { isSuperAdmin } = useAuth();

  const [data, setData] = useState({ partners: [], meta: null });
  const [storeList, setStoreList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', kycStatus: '', page: 1 });
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [credentialsFor, setCredentialsFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await partnersApi.list({
        page: filters.page,
        limit: 20,
        search: filters.search || undefined,
        kycStatus: filters.kycStatus || undefined,
      });
      setData(res.data);
    } catch (err) {
      toast.error('Could not load partners', err.message);
    } finally {
      setLoading(false);
    }
  }, [filters, toast]);

  useEffect(() => { load(); }, [load]);

  // A rider submitting documents should surface here immediately.
  useOrderEvents((event) => { if (event === 'partner:kyc-submitted') load(); });

  useEffect(() => {
    if (isSuperAdmin) storesApi.list({ limit: 100 }).then((r) => setStoreList(r.data.stores)).catch(() => {});
    document.title = 'Delivery Partners · UrbanClean';
  }, [isSuperAdmin]);

  const review = async (partner, action) => {
    const reason = action === 'reject'
      ? window.prompt('Why is this application being rejected?')
      : undefined;
    if (action === 'reject' && reason === null) return;

    try {
      await partnersApi.reviewKyc(partner._id, { action, reason });
      toast.success(action === 'approve' ? 'Partner approved' : 'Partner rejected');
      load();
    } catch (err) {
      toast.error('Could not update the application', err.message);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Delivery Partners</h1>
          <div className="sub">Create rider accounts, set shifts and pay, track performance</div>
        </div>
        <Can permission="partner.create">
          <button className="btn btn-blue" onClick={() => setEditing({})}>+ New delivery partner</button>
        </Can>
      </div>

      <div className="toolbar">
        <input
          className="input"
          placeholder="Search name or mobile"
          value={filters.search}
          onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value, page: 1 }))}
          style={{ minWidth: 230 }}
        />
        <select className="select" value={filters.kycStatus} onChange={(e) => setFilters((f) => ({ ...f, kycStatus: e.target.value, page: 1 }))}>
          <option value="">All applications</option>
          <option value="submitted">Awaiting review</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="pending">Incomplete</option>
        </select>
      </div>

      <div className="card">
        {loading ? <Loading /> : data.partners.length === 0 ? (
          <Empty title="No delivery partners yet" body="Riders appear here once they sign up in the partner app." />
        ) : (
          <>
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Partner</th><th>Shift</th><th>Vehicle</th><th>Pay</th>
                    <th className="center">Deliveries</th><th>Status</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {data.partners.map((p) => (
                    <tr key={p._id}>
                      <td>
                        <div className="strong">{p.name || 'Unnamed'}</div>
                        <div className="muted" style={{ fontSize: 12 }}>{p.mobile}</div>
                        <span className={`badge badge-${p.isOnline ? 'green' : 'gray'}`} style={{ marginTop: 4 }}>
                          {p.isOnline ? 'Online' : 'Offline'}
                        </span>
                        {p.isBlocked ? <span className="badge badge-red" style={{ marginLeft: 4 }}>Suspended</span> : null}
                      </td>
                      <td className="muted" style={{ fontSize: 12.5 }}>
                        {p.shift?.startTime || '—'} – {p.shift?.endTime || '—'}
                        <div style={{ fontSize: 11.5 }}>{p.shift?.hoursPerDay || 0} hrs/day</div>
                      </td>
                      <td className="muted">
                        {p.vehicle?.type || '—'}
                        <div className="mono" style={{ fontSize: 12 }}>{p.vehicle?.number}</div>
                      </td>
                      <td>
                        <span className="badge badge-blue">{p.employmentType === 'full-time' ? 'Salaried' : 'Commission'}</span>
                        <div className="muted" style={{ fontSize: 11.5, marginTop: 3 }}>
                          {p.employmentType === 'full-time' ? `${money(p.monthlySalary)}/mo` : `${money(p.commissionPerOrder)}/order`}
                          {p.hasIncentive ? ` + ${money(p.incentivePerOrder)} incentive` : ''}
                        </div>
                      </td>
                      <td className="center">
                        {p.totalDeliveries}
                        <div className="muted" style={{ fontSize: 11.5 }}>{money(p.totalEarnings)}</div>
                      </td>
                      <td>
                        <span className={`badge badge-${KYC_TONE[p.kycStatus] || 'gray'}`}>{p.kycStatus}</span>
                      </td>
                      <td className="right nowrap">
                        {p.kycStatus === 'submitted' ? (
                          <Can permission="partner.update">
                            <button className="btn btn-sm btn-blue" onClick={() => review(p, 'approve')}>Approve</button>
                            <button className="btn btn-sm btn-ghost" style={{ marginLeft: 6, color: 'var(--red)' }} onClick={() => review(p, 'reject')}>
                              Reject
                            </button>
                          </Can>
                        ) : (
                          <Can permission="partner.update">
                            <button className="btn btn-sm btn-ghost" onClick={() => setEditing(p)}>Edit</button>
                            <button className="btn btn-sm btn-ghost" style={{ marginLeft: 6 }} onClick={() => setCredentialsFor(p)}>
                              Login
                            </button>
                            <button className="btn btn-sm btn-ghost" style={{ marginLeft: 6 }} onClick={() => setViewing(p)}>
                              Pay
                            </button>
                          </Can>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination meta={data.meta} onPage={(page) => setFilters((f) => ({ ...f, page }))} />
          </>
        )}
      </div>

      <PartnerForm
        partner={editing}
        storeList={storeList}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />

      <CredentialsModal partner={credentialsFor} onClose={() => setCredentialsFor(null)} />

      <PartnerEditor
        partner={viewing}
        storeList={storeList}
        onClose={() => setViewing(null)}
        onSaved={() => { setViewing(null); load(); }}
      />
    </>
  );
}

function PartnerEditor({ partner, storeList, onClose, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!partner) return;
    setForm({
      name: partner.name || '',
      employmentType: partner.employmentType || 'commission',
      monthlySalary: partner.monthlySalary || 0,
      commissionPerOrder: partner.commissionPerOrder || 0,
      commissionPercent: partner.commissionPercent || 0,
      store: partner.store?._id || partner.store || '',
      isBlocked: Boolean(partner.isBlocked),
    });
  }, [partner]);

  if (!partner) return null;

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const save = async () => {
    setBusy(true);
    try {
      await partnersApi.update(partner._id, { ...form, store: form.store || null });
      toast.success('Partner updated');
      onSaved();
    } catch (err) {
      toast.error('Could not save', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      title={`Manage — ${partner.name || partner.mobile}`}
      onClose={onClose}
      footer={<>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
      </>}
    >
      <div className="field">
        <label>Name</label>
        <input className="input" value={form.name || ''} onChange={set('name')} />
      </div>

      <div className="field">
        <label>Employment type</label>
        <select className="select" value={form.employmentType} onChange={set('employmentType')}>
          <option value="commission">Commission-based</option>
          <option value="full-time">Full-time (salaried)</option>
        </select>
        <div className="hint">Switchable at any time — the earnings sheet follows this setting.</div>
      </div>

      {form.employmentType === 'full-time' ? (
        <div className="field">
          <label>Monthly salary</label>
          <input className="input" type="number" value={form.monthlySalary} onChange={set('monthlySalary')} />
        </div>
      ) : (
        <div className="row">
          <div className="field">
            <label>Commission per order</label>
            <input className="input" type="number" value={form.commissionPerOrder} onChange={set('commissionPerOrder')} />
          </div>
          <div className="field">
            <label>Commission % of order value</label>
            <input className="input" type="number" value={form.commissionPercent} onChange={set('commissionPercent')} />
          </div>
        </div>
      )}

      {storeList.length ? (
        <div className="field">
          <label>Assigned store</label>
          <select className="select" value={form.store || ''} onChange={set('store')}>
            <option value="">Unassigned</option>
            {storeList.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
          </select>
        </div>
      ) : null}

      <div className="field">
        <label className="switch">
          <input type="checkbox" checked={form.isBlocked} onChange={set('isBlocked')} />
          <span className="track" />
          <span>Suspend this partner</span>
        </label>
        <div className="hint">A suspended rider goes offline and cannot be assigned new orders.</div>
      </div>

      <div className="card" style={{ background: 'var(--blue-50)', border: 'none', boxShadow: 'none' }}>
        <div className="card-body" style={{ padding: 13, fontSize: 12.5 }}>
          <div className="strong" style={{ color: 'var(--navy)', marginBottom: 6 }}>KYC on file</div>
          <div className="muted" style={{ lineHeight: 1.8 }}>
            Aadhaar: {partner.kyc?.aadhaarNumber || '—'}<br />
            Driving licence: {partner.kyc?.drivingLicenseNumber || '—'}<br />
            Vehicle: {partner.vehicle?.type} {partner.vehicle?.number}<br />
            Bank: {partner.bank?.accountNumber ? `${partner.bank.bankName} · ${partner.bank.ifsc}` : '—'}<br />
            Submitted: {partner.kyc?.submittedAt ? dateOnly(partner.kyc.submittedAt) : '—'}
          </div>
        </div>
      </div>
    </Modal>
  );
}
